import { useState } from 'react';
import imageCompression from 'browser-image-compression';
import { BASE_URL } from '../utils/Constants';

const useFileUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [error, setError] = useState(null);

  const uploadFile = async (file) => {
    setUploading(true);
    setIsCompressing(true);
    setError(null);

    try {
      const MAX_DOCUMENT_SIZE_MB = 1; // 1MB
      const MAX_IMAGE_SIZE_MB = 50; // 50MB

      let fileToUpload = file;
      const isImage = file.type.startsWith('image/');

      if (isImage) {
        if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
          throw new Error(
            `Image file '${file.name}' is too large. Maximum allowed size is ${MAX_IMAGE_SIZE_MB}MB.`,
          );
        }
        const compressionOptions = {
          maxSizeMB: 1, // (max file size in MB) - This is for the *compressed* image size
          maxWidthOrHeight: 1920, // compressed image's max width or height
          useWebWorker: true, // optional, use multi-thread web worker for compression
          fileType: 'image/webp', // output file type
        };
        fileToUpload = await imageCompression(file, compressionOptions);
      } else {
        setIsCompressing(false); // No compression needed for non-image files
        if (file.size > MAX_DOCUMENT_SIZE_MB * 1024 * 1024) {
          throw new Error(
            `Document file '${file.name}' is too large. Maximum allowed size is ${MAX_DOCUMENT_SIZE_MB}MB.`,
          );
        }
      }

      const formData = new FormData();
      formData.append('file', fileToUpload);

      // Add content type explicitly for better handling
      formData.append('contentType', fileToUpload.type);
      formData.append('fileName', fileToUpload.name);

      const response = await fetch(`${BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errData = await response.json();
        console.error('Upload failed:', errData);
        throw new Error(errData?.error || 'Upload failed');
      }

      const result = await response.json();
      setUploading(false);
      setIsCompressing(false);
      return result.url;
    } catch (err) {
      setUploading(false);
      setIsCompressing(false);
      setError('Error uploading file');
      console.error('File upload error:', err);
      throw err;
    }
  };

  return { uploadFile, uploading, isCompressing, error };
};

export default useFileUpload;
